import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuthStore } from '@shared/store/auth.store';
import { PATHS } from '@shared/router/paths';
import { RouteSplashScreen } from '@shared/router/RouteSplashScreen';
import { SessionUnavailablePage } from '@shared/router/SessionUnavailablePage';

/**
 * Gate for authenticated areas. While the session is bootstrapping we render a
 * splash instead of redirecting, so a logged-in user reloading the page — or
 * arriving from the application switcher — is not bounced to the login
 * screen. Unauthenticated users are redirected to login with the attempted
 * location preserved for post-login return; when the session they arrived
 * with was refused, the sign-in page says it ended. When it could not be
 * checked at all, a retry is offered instead (never a redirect loop: the
 * retry screen and the login page are both terminal until the user acts).
 */
export function ProtectedRoute() {
  const location = useLocation();
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const isInitializing = useAuthStore((state) => state.isInitializing);
  const sessionRestore = useAuthStore((state) => state.sessionRestore);

  if (isInitializing) {
    return <RouteSplashScreen />;
  }

  if (!isAuthenticated) {
    if (sessionRestore === 'unavailable') return <SessionUnavailablePage />;
    return (
      <Navigate
        to={{
          pathname: PATHS.auth.login,
          search: sessionRestore === 'ended' ? '?reason=expired' : '',
        }}
        replace
        state={{ from: location }}
      />
    );
  }

  return <Outlet />;
}
